// Вложения запроса, разложенные по каналам бэкенда.
//
// Бэкенд ждёт fully-qualified URL в отдельных массивах по виду медиа, а
// картинки — ещё и разделёнными на кадры и референсы. `s3Url` приходит готовым
// из ответа загрузки и на фронте не пересобирается (dev=Backblaze, prod=AWS).

import type { ChatAttachment, ReferenceMetadata } from "~/entities/chat";
import type { ImageInputRole } from "~/shared/data/chat-models";
import type { PendingUpload } from "~/shared/store/types";

import { buildImageChannels } from "./role-payload";

export interface AttachmentPayload {
    inputImageUrls: string[];
    referenceImageUrls: string[];
    inputVideoUrls: string[];
    inputAudioUrls: string[];
    references: ReferenceMetadata[];
}

/**
 * Каналы вложений для запроса генерации.
 *
 * В запрос идут только доехавшие до хранилища вложения: у остальных ещё нет
 * ссылки, и провайдер получил бы пустое место вместо кадра.
 *
 * Раскладка кадров и референсов приходит отдельно (`pending`): роль кадра и
 * канал живут на вложении промпт-бара, а при повторе — на снимке генерации,
 * потому что очередь бара к тому времени пуста или занята следующим запросом.
 *
 * Реестр референсов наполняется вложениями с посчитанным на фронте sha256: бэк
 * дедуплицирует по хэшу и пишет провенанс-связь, не скачивая файл повторно. Без
 * хэша (исторические вложения, артефакты из выбора уже сгенерированного) в
 * реестр не идут.
 */
export function buildAttachmentPayload(
    inputAttachments: readonly ChatAttachment[],
    imageRoles: readonly ImageInputRole[],
    pending: readonly PendingUpload[],
): AttachmentPayload {
    const uploaded = inputAttachments.filter((a) => a.status === "uploaded");
    const { inputImageUrls, referenceImageUrls } = buildImageChannels(
        uploaded.filter((a) => a.kind === "image"),
        imageRoles,
        pending,
    );

    return {
        inputImageUrls,
        referenceImageUrls,
        inputVideoUrls: uploaded.filter((a) => a.kind === "video").map((a) => a.s3Url),
        inputAudioUrls: uploaded.filter((a) => a.kind === "audio").map((a) => a.s3Url),
        references: uploaded
            .filter((a): a is ChatAttachment & { sha256: string } => a.sha256 !== null)
            .map((a) => ({ sha256: a.sha256, kind: a.kind, bytes: a.sizeBytes, url: a.s3Url })),
    };
}
